import { useEffect, useState } from "react";
import { Alert, Button, InputNumber, Space, Typography, notification } from "antd";
import { ancanaApi } from "../../../api/ancanaApi";
import RecordTable from "../../RecordTable";
import { IUnitPaymentPlan, UnitPaymentPlanModel } from "../../../types/models/economics";
import PlanPreview from "./PlanPreview";

/** The brochure template draws payment-plan layouts for up to this many plans
 * (`when_listing.payment_plans.count`); readiness blocks a listing with more. */
const MAX_BROCHURE_PLANS = 4;

interface UnitTerms {
  holding_deposit: string | null;
  currency_iso: string;
}

/**
 * How a buyer can pay for one unit: the holding deposit that reserves it, and
 * the plans a brochure lists side by side.
 *
 * The deposit is a field on the unit, not on the listing, so it saves on its
 * own button rather than with the wizard's Finish — the same way the plan rows
 * save as they are edited.
 */
function PaymentPlansPanel({ unitId }: { unitId: number | string }) {
  const [terms, setTerms] = useState<UnitTerms | null>(null);
  const [deposit, setDeposit] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    ancanaApi.get<UnitTerms>(`housing-units/${unitId}/`, { params: { fields: 'holding_deposit,currency_iso' } })
      .then(({ data }) => {
        if (!live) return;
        setTerms(data);
        setDeposit(data.holding_deposit === null ? null : Number(data.holding_deposit));
      });
    return () => { live = false; };
  }, [unitId]);

  const saved = terms?.holding_deposit === null || terms?.holding_deposit === undefined
    ? null : Number(terms.holding_deposit);

  const saveDeposit = async () => {
    setSaving(true);
    try {
      const { data } = await ancanaApi.patch<UnitTerms>(`housing-units/${unitId}/`, { holding_deposit: deposit });
      setTerms(current => ({ ...(current as UnitTerms), holding_deposit: data.holding_deposit }));
      notification.success({ message: 'Holding deposit saved', placement: 'bottomRight' });
    } catch (e: any) {
      notification.error({ message: 'Could not save the holding deposit', description: e.message, placement: 'bottomRight' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Typography.Title level={5}>Holding deposit</Typography.Title>
      <Space align="start">
        <InputNumber<number>
          aria-label="Holding deposit"
          min={0}
          value={deposit ?? undefined}
          onChange={value => setDeposit(value)}
          addonBefore="$"
          addonAfter={terms?.currency_iso}
          disabled={!terms}
          style={{ width: 260 }}
        />
        <Button onClick={saveDeposit} loading={saving} disabled={!terms || deposit === saved}>
          Save
        </Button>
      </Space>
      <Typography.Paragraph type="secondary" className="mt-sm">
        Leave empty if the unit takes no deposit; the brochure then drops the deposit line.
      </Typography.Paragraph>

      <RecordTable<IUnitPaymentPlan>
        className="mt-lg"
        recordModel={UnitPaymentPlanModel}
        recordsetFilters={{ unit: unitId }}
        customRecordName="Payment Plans"
        footer={(plans) => (
          <>
            {plans.length > MAX_BROCHURE_PLANS ? (
              <Alert
                type="warning"
                showIcon
                message={`The brochure has layouts for up to ${MAX_BROCHURE_PLANS} plans; this unit has ${plans.length}, so its brochure is blocked.`}
              />
            ) : (
              <Typography.Text type="secondary">
                Amounts are in {terms?.currency_iso || "the unit's currency"}. The brochure shows up to {MAX_BROCHURE_PLANS} plans.
              </Typography.Text>
            )}
            {/* The rows themselves are the version: any edit, add or delete refetches. */}
            <PlanPreview unitId={unitId} version={JSON.stringify(plans)} />
          </>
        )}
        noPagination
      />
    </div>
  );
}

export default PaymentPlansPanel;
